/* eslint-disable camelcase */
import { useRNBSelector } from "../../../store/rootReducer"
import { useDispatch } from "react-redux"
import React, { Fragment, Suspense, useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { getFiduciaryKPIsThunk, KPIFile } from "../../../store/ducks/kpis.ducks"
import styled from "styled-components/macro"
import { FileRow } from "../../../components/FileRow"
import { useIntl } from "react-intl"
import frFR from "date-fns/locale/fr"
import * as Ct from "ldlj"
import { Companies } from "../../../store/ducks/companies.ducks"
import { getCurrentUserThunk } from "../../../store/ducks/user.ducks"
import { useNavigate } from "react-router-dom"
import { SortButton } from "../../../components/Commons/SortButton"
import { boxShadow, colors } from "../../../styles/design.config"
import { ReactComponent as DownloadFile } from "../../../assets/download-file.svg"
import { CSVLink } from "react-csv"
import {
  searchOnCompaniesData,
  sortKpisByAccountingType,
  sortKpisByAllFdCount,
  sortKpisByBuy,
  sortKpisByCreatedAt,
  sortKpisByDeactivated,
  sortKpisByName,
  sortKpisByPending,
  sortKpisByReference,
  sortKpisBySell,
  sortKpisBySiren,
  sortKpisByfdOnPeriod,
} from "../../../utils/fiduciary"
import { ReactComponent as Search } from "../../../assets/search.svg"

const Calendar = {
  sunday: "Di",
  monday: "Lu",
  tuesday: "Ma",
  wednesday: "Me",
  thursday: "Je",
  friday: "Ve",
  saturday: "Sa",
  ok: "OK",
  today: "Aujourd'hui",
  yesterday: "Hier",
  hours: "Heures",
  minutes: "Minutes",
  seconds: "Secondes",
  /**
   * Format of the string is based on Unicode Technical Standard #35:
   * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
   **/
  formattedMonthPattern: "MMM yyyy",
  formattedDayPattern: "dd MMM yyyy",
  dateLocale: frFR,
}

const locale = {
  Calendar,
  DatePicker: {
    ...Calendar,
  },
  DateRangePicker: {
    ...Calendar,
    last7Days: "7 derniers jours",
  },
}

const DateRangePicker = React.lazy(() => import("rsuite/DateRangePicker"))
const CustomProvider = React.lazy(() => import("rsuite/CustomProvider"))

export const Indicators = () => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const [asc, setAsc] = useState(true)
  const [range, setRange] = useState<[Date, Date]>([
    DateTime.now().set({ day: 1 }).toJSDate(),
    DateTime.now().toJSDate(),
  ])
  const navigate = useNavigate()

  const userTypology = useRNBSelector((state) => state.user.typology)

  if (userTypology === "customer") navigate("/unauthorized")

  const companies: Companies = useRNBSelector(
    (state) => state.companies.companies
  )
  const fiduciary = useRNBSelector((state) => state.fiduciary)
  const files = useRNBSelector((state) => state.kpis.files)
  const [sortedCompanies, setSortedCompanies] = useState(
    Object.values(companies)
  )

  const [search, setSearch] = useState("")
  const [companiesToDisplay, setCompaniesToDisplay] = useState<KPIFile[]>([])
  const [CSVData, setCSVData] = useState<
    {
      name: string
      deposit_range: number
      deposit: number
      buy: number
      sell: number
      pending: number
      deactivated: number
    }[]
  >([])

  const fiduciaryCreationDate = new Date(
    fiduciary.created_at !== ""
      ? fiduciary.created_at
      : DateTime.now().set({ day: 1 }).toJSDate()
  )

  const TableHeaders = [
    { label: "", key: "name" },
    { label: "Tous les dépôts", key: "deposit" },
    { label: "Dépôts sur la période", key: "deposit_range" },
    { label: "Achats sur la période", key: "buy" },
    { label: "Ventes sur la période", key: "sell" },
    { label: "En cours sur la période", key: "pending" },
    { label: "GED sur la période", key: "deactivated" },
  ]

  const CSVHeaders = [
    { label: "Nom", key: "name" },
    { label: "Siren", key: "siren" },
    { label: "Référence", key: "accounting_software_reference" },
    { label: "Créée par", key: "created_by" },
    { label: "Créée le", key: "created_at" },
    { label: "Type de dossier", key: "accounting_type" },
    { label: "Tous les dépôts", key: "deposit" },
    { label: "Dépôts sur la période", key: "deposit_range" },
    { label: "Achats sur la période", key: "buy" },
    { label: "Ventes sur la période", key: "sell" },
    { label: "En cours sur la période", key: "pending" },
    { label: "GED sur la période", key: "deactivated" },
  ]

  const getCSVFileName = () => {
    const fiduciaryName = fiduciary.name
    const firstDateDay =
      range[0].getDate().toString().length === 1
        ? "0" + range[0].getDate()
        : range[0].getDate()
    const firstDateMonth =
      (range[0].getMonth() + 1).toString().length === 1
        ? "0" + (range[0].getMonth() + 1)
        : range[0].getMonth() + 1
    const firstDateYear = range[0].getFullYear()
    const secondDateDay =
      range[1].getDate().toString().length === 1
        ? "0" + range[1].getDate()
        : range[1].getDate()
    const secondDateMonth =
      (range[1].getMonth() + 1).toString().length === 1
        ? "0" + (range[1].getMonth() + 1)
        : range[1].getMonth() + 1
    const secondDateYear = range[1].getFullYear()
    return `indicateurs_${fiduciaryName}_${firstDateDay}-${firstDateMonth}-${firstDateYear}_${secondDateDay}-${secondDateMonth}-${secondDateYear}`
  }

  const generateForCSV = (companiesSearched: KPIFile[]) => {
    const data = companiesSearched.map((company) => {
      return {
        name: company.company_name,
        siren: company.siren,
        accounting_software_reference: company.accounting_software_reference,
        created_by: company.email,
        created_at: company.created_at,
        accounting_type:
          company.accounting_type === "cash_accounting"
            ? "Comptabilité de trésorerie"
            : "Comptabilité d'engagement",
        deposit: company.total_fd_count || 0,
        deposit_range: company.total_fd_period || 0,
        buy: company.buy || 0,
        sell: company.sell || 0,
        pending: company.pending || 0,
        deactivated: company.deactivated || 0,
      }
    })
    setCSVData(data)
  }

  type SortOptionsValues =
    | "companyName"
    | "allFdCount"
    | "fdOnPeriod"
    | "buy"
    | "sell"
    | "pending"
    | "deactivated"
    | "reference"
    | "siren"
    | "createdAt"
    | "accountingType"

  const sorter = (asc: boolean) => (option: SortOptionsValues) => {
    let companiesSorted: KPIFile[] = []
    if (option === "companyName") {
      companiesSorted = sortKpisByName(companiesToDisplay, asc)
    } else if (option === "allFdCount") {
      companiesSorted = sortKpisByAllFdCount(companiesToDisplay, asc)
    } else if (option === "fdOnPeriod") {
      companiesSorted = sortKpisByfdOnPeriod(companiesToDisplay, asc)
    } else if (option === "buy") {
      companiesSorted = sortKpisByBuy(companiesToDisplay, asc)
    } else if (option === "sell") {
      companiesSorted = sortKpisBySell(companiesToDisplay, asc)
    } else if (option === "pending") {
      companiesSorted = sortKpisByPending(companiesToDisplay, asc)
    } else if (option === "deactivated") {
      companiesSorted = sortKpisByDeactivated(companiesToDisplay, asc)
    } else if (option === "reference") {
      companiesSorted = sortKpisByReference(companiesToDisplay, asc)
    } else if (option === "siren") {
      companiesSorted = sortKpisBySiren(companiesToDisplay, asc)
    } else if (option === "createdAt") {
      companiesSorted = sortKpisByCreatedAt(companiesToDisplay, asc)
    } else if (option === "accountingType") {
      companiesSorted = sortKpisByAccountingType(companiesToDisplay, asc)
    }
    setAsc(!asc)
    setCompaniesToDisplay(companiesSorted)
    generateForCSV(companiesSorted)
  }

  const sortOptions: Ct.OptionList<SortOptionsValues> = [
    { value: "companyName", label: "Nom de société", sortType: "string" },
    { value: "allFdCount", label: "Tous les dépôts", sortType: "number" },
    {
      value: "fdOnPeriod",
      label: "Dépôts sur la période",
      sortType: "number",
    },
    { value: "buy", label: "Achats sur la période", sortType: "number" },
    { value: "sell", label: "Ventes sur la période", sortType: "number" },
    { value: "pending", label: "En Cours sur la période", sortType: "number" },
    { value: "deactivated", label: "GED sur la période", sortType: "number" },
    { value: "reference", label: "Référence", sortType: "string" },
    { value: "siren", label: "Siren", sortType: "string" },
    {
      value: "accountingType",
      label: "Type de comptabilité",
      sortType: "string",
    },
    { value: "createdAt", label: "Date de création", sortType: "string" },
  ]

  useEffect(() => {
    setSortedCompanies(Object.values(companies))
  }, [companies])

  useEffect(() => {
    dispatch(getCurrentUserThunk())
  }, [dispatch])

  useEffect(() => {
    if (fiduciary.id === 0) {
      return
    }
    if (fiduciaryCreationDate) {
      setRange([fiduciaryCreationDate, DateTime.now().toJSDate()])
    }
  }, [fiduciary.id])

  useEffect(() => {
    if (fiduciary.id === 0) {
      return
    }
    dispatch(
      getFiduciaryKPIsThunk(fiduciary.id, {
        startDate: DateTime.fromJSDate(range[0]).toISODate(),
        endDate: DateTime.fromJSDate(range[1]).toISODate(),
      })
    )
  }, [dispatch, fiduciary.id, range])

  useMemo(() => {
    if (sortedCompanies && sortedCompanies.length > 0 && files) {
      const companiesSearched = searchOnCompaniesData(
        Object.values(files),
        search
      )

      setCompaniesToDisplay(companiesSearched)
      generateForCSV(companiesSearched)
    }
  }, [
    sortedCompanies,
    files,
    range,
    JSON.stringify(Object.values(files)),
    search,
  ])

  return (
    <Wrapper>
      <StyledSection>
        <StyledIndicatorsSection>
          <StyledPickerHeader>
            <Ct.Input
              label="Rechercher"
              value={search}
              suffix={<Search />}
              maxWidth={30}
              onChange={(e) => {
                setSearch(e.target.value)
              }}
            />
            <GrowingDiv>
              <Suspense fallback={<Ct.SpinningLoader />}>
                <Ct.Text
                  text={intl.formatMessage({ id: "office.fiduciary.range" })}
                />
                <CustomProvider locale={locale}>
                  <DateRangePicker
                    appearance="subtle"
                    value={range}
                    isoWeek={true}
                    cleanable={false}
                    character={intl.formatMessage({
                      id: "office.fiduciary.to",
                    })}
                    onChange={(value) => setRange(value as never)}
                    ranges={[]}
                    format={"dd MM yyyy"}
                  />
                </CustomProvider>
              </Suspense>
            </GrowingDiv>
            <StyledCSVLink
              data={CSVData}
              headers={CSVHeaders}
              filename={getCSVFileName()}
            >
              <DownloadFile />
              <Ct.Spacer width={1} />
              <Ct.Text
                text={intl.formatMessage({
                  id: "fiduciary.indicators.generate-csv",
                })}
                textStyle={{
                  fontWeight: 500,
                  fontSize: 2,
                  color: "white",
                  cursor: "pointer",
                }}
              />
            </StyledCSVLink>
            <Ct.Spacer width={3} />
            <SortButton
              sortMethod={sorter(asc)}
              options={sortOptions}
              defaultOption={sortOptions[0]}
              intl={intl}
              asc={!asc}
            />
          </StyledPickerHeader>
          <Ct.Spacer height={3} />

          <HeaderTable>
            {TableHeaders.map((header, index) => (
              <Head key={index}>
                {index === 0 ? (
                  <>
                    <Ct.Spacer height={0} width={12} />
                    <Ct.Spacer height={0} width={12} />
                    <Ct.Spacer height={0} width={12} />
                    <Ct.Spacer height={0} width={5} />
                  </>
                ) : (
                  <>
                    <Ct.Spacer height={0} width={1} />
                    <Ct.Text
                      text={header.label}
                      textStyle={{
                        fontWeight: 600,
                      }}
                    />
                  </>
                )}
              </Head>
            ))}
          </HeaderTable>

          <StyledFiles>
            {companiesToDisplay.map((company, idx) => (
              <Fragment key={idx}>
                <FileRow
                  company={companies[company.id]}
                  companyKPIs={company}
                />
              </Fragment>
            ))}
          </StyledFiles>
        </StyledIndicatorsSection>
      </StyledSection>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-bottom: 4rem;
  display: flex;
  box-sizing: border-box;
  height: 100%;
`

const StyledSection = styled.section`
  border-radius: 0 2.5rem 2.5rem;
  background-color: ${colors.white};
  box-shadow: ${boxShadow};
  padding: 5rem 3rem 4rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`
const StyledPickerHeader = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;

  > .rs-picker-daterange > .rs-picker-toggle.rs-btn {
    width: 34rem;
    padding-right: 0;
    padding-left: 0.5rem;
  }

  > .rs-picker-daterange > .rs-picker-toggle.rs-btn > svg {
    top: 6px;
  }

  > span.rs-picker-toggle-value,
  div.rs-picker-toggle.rs-btn.rs-btn-subtle {
    width: auto;
  }

  > .rs-picker-daterange
    > .rs-picker-toggle.rs-picker-toggle-active.rs-btn.rs-btn-subtle,
  > .rs-picker-daterange
    > .rs-picker-toggle.rs-picker-toggle-active.rs-btn.rs-btn-subtle
    > input,
  > .rs-picker-daterange > div.rs-picker-toggle.rs-btn.rs-btn-subtle:hover {
    background-color: transparent;
    color: transparent;
    cursor: pointer;
    width: auto;
  }
`
const GrowingDiv = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`
const StyledFiles = styled.div`
  overflow: auto;
  height: 100%;

  /* FIREFOX */
  scrollbar-color: ${colors.rock} transparent;
  scrollbar-width: thin !important;

  /* CHROME */
  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px transparent;
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${colors.rock};
    border-radius: 3px;
    width: 4px;
  }
`
const StyledIndicatorsSection = styled.section`
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding-top: 1rem;
`
const StyledCSVLink = styled(CSVLink)`
  height: 6rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 2rem;
  border-radius: 1.25rem;
  background-image: linear-gradient(125.36deg, #5982f3 5.35%, #8dcdf8 94.88%);
`
const HeaderTable = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 4rem 2rem 2rem;
`
const Head = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  align-items: center;
`
